import React, { useState, useEffect, useContext } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import Select from "react-select";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EmailAuthContext } from "../../../core/common/context/EmailAuthContext";
import { useDispatch, useSelector } from "react-redux";
import {
  profileEvents,
  addOrUpdateMeeting,
  deleteMeeting,
} from "../../../core/data/redux/slices/EventSlice";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
// import { setSelectedContact } from "../../../core/data/redux/slices/SelectedContactSlice\";
import api from "../../../core/axios/axiosInstance";
import dayjs from "dayjs";
import { DatePicker, TimePicker, Modal, Button, Spin } from "antd";
import { all_routes } from "../../router/all_routes";
// import { saveContact } from "../../../core/data/redux/slices/ContactSlice";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useDebounce } from "../../../core/hooks/useDebounce";
import "./calendar.css";
import { meetingType } from "../../../core/common/selectoption/selectoption";
import { FiCopy } from "react-icons/fi";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  IoAlertCircle,
  IoCall,
  IoCallOutline,
  IoLocationSharp,
  IoTicketOutline,
} from "react-icons/io5";


const Calendar = () => {

  const [allContacts, setAllContacts] = useState([])
  const [defaultEvents, setDefaultEvents] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [selectedMeetingType, setSelectedMeetingType] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);


  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const today = dayjs();
  const [selectedDate, setSelectedDate] = useState(today.format("YYYY-MM-DD"));

  const userProfile = useSelector((state) => state.profile);
  const eventsState = useSelector((state) => state.event);
  const events = eventsState?.data || eventsState || [];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [meetingFormData, setMeetingFormData] = useState({
    meeting_id: "",
    meetingDescription: "",
    meetingTitle: "",
    meetingStartDate: dayjs(),
    meetingStartTime: dayjs("00:00:00", "HH:mm:ss"),
    meetingProvider: "",
    meetingType: "",
    meetingLocation: ""
  });
  const getFormattedDate = (startTime) => {
    const fmstring = dayjs(startTime, "HH:mm").format("hh:mm A");
    if (!fmstring || fmstring === "Invalid Date") {
      return "No time specified";
    }
    return fmstring;
  };
  const location = useLocation();
  const isCalendar = location.pathname.includes("calendar");
  dayjs.extend(customParseFormat);
  const route = all_routes;
  useEffect(() => {
    setDefaultEvents(events);
  }, [events]);

  useEffect(() => {
    const eventsArr = eventsState?.data || eventsState || [];
    if (!Array.isArray(eventsArr) || eventsArr.length === 0) {
      dispatch(profileEvents()).finally(() => setIsInitialLoad(false));
    } else {
      setIsInitialLoad(false);
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    setIsInitialLoad(true);
    try {
      await dispatch(profileEvents());
    } finally {
      setIsInitialLoad(false);
      setRefreshing(false);
    }
  };

  // Open create-event modal when navigated with ?action=create-event
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "create-event") {
      resetMeetingForm();
      setIsMeetingModalOpen(true);
    }
  }, [location.search]);

  const handleCopy = async (meeting) => {


    await navigator.clipboard.writeText(meeting.link);
    setCopiedId(meeting.event_id);

    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };
  // Fetch contacts and leads with search functionality
  const fetchContactsAndLeads = async (search = "") => {
    try {
      setIsLoadingContacts(true);
      const params = search ? { search } : {};
      const response = await api.get("/getAllContactsOrLeads/ForEvent", {
        params,
      });

      const transformed = response.data.data.map((item) => {
        const name = `${item.firstname || ""} ${item.lastname || ""}`.trim();
        const email = item.emailAddresses?.[0] || "";
        const categoryLabel = item.category === "lead" ? "Lead" : "Contact";
        return {
          value: item.id,
          label: email
            ? `${name} (${email}) - ${categoryLabel}`
            : `${name} - ${categoryLabel}`,
          category: item.category,
        };
      });
      setAllContacts(transformed);
    } catch (error) {
      console.log(
        error.response?.data?.message || "Error fetching contacts and leads"
      );
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // Debounced search effect - only fetch when there's a meaningful search query
  useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery.trim().length >= 1) {
      fetchContactsAndLeads(debouncedSearchQuery);
    } else {
      // Clear contacts when search is too short or cleared
      setAllContacts([]);
    }
  }, [debouncedSearchQuery]);

  const handleMeetingSubmit = async () => {
    // Validation

    if (
      meetingFormData.meetingType === "online" &&
      !meetingFormData.meetingProvider
    ) {
      dispatch(
        showToast({
          message: "Please select a meeting provider",
          variant: "danger",
        })
      );
      return;
    }


    if (!meetingFormData.meetingTitle?.trim()) {
      dispatch(
        showToast({
          message: "Meeting title is required",
          variant: "danger",
        })
      );
      return;
    }

    if (!meetingFormData.meetingDescription?.trim()) {
      dispatch(
        showToast({
          message: "Meeting description is required",
          variant: "danger",
        })
      );
      return;
    }

    if (!selectedOption) {
      dispatch(
        showToast({
          message: "Please select a contact or lead",
          variant: "danger",
        })
      );
      return;
    }

    setIsLoading(true)

    // Prepare meeting data - force offline type
    const meetingData = {
      contact_id: selectedOption.value,
      category: selectedOption.category,
      meetingTitle: meetingFormData.meetingTitle,
      meetingDescription: meetingFormData.meetingDescription,
      meetingStartDate: meetingFormData.meetingStartDate.format("YYYY-MM-DD"),
      meetingStartTime: meetingFormData.meetingStartTime.format("HH:mm"),
      meetingType: meetingFormData.meetingType == "online" ? "online" : "offline",
      meetingLocation:
        meetingFormData.meetingType === "offline"
          ? meetingFormData.meetingLocation
          : "",
      meetingProvider:
        meetingFormData.meetingType === "online"
          ? meetingFormData.meetingProvider
          : null,

    };

    // Add meeting_id if updating
    if (meetingFormData.meeting_id) {
      meetingData.meeting_id = meetingFormData.meeting_id;
    }


    try {
      await dispatch(addOrUpdateMeeting(meetingData)).unwrap();
      // Refresh events for calendar
      dispatch(profileEvents());

      // Reset form and close modal
      resetMeetingForm();
      setIsMeetingModalOpen(false);
    } catch (error) {
      console.error("Error saving meeting:", error);
    }
    finally {
      setIsLoading(false)
    }
  };

  const handleEditMeeting = (event) => {

    setIsMeetingModalOpen(true)
    console.log(event, "hjgfdsfghj");

    setMeetingFormData({
      meeting_id: event.event_id,
      meetingTitle: event.title,
      meetingDescription: event.description || "",
      meetingStartDate: event.startTime
        ? dayjs(
            `${dayjs(event.start).format("YYYY-MM-DD")} ${event.startTime}`,
            "YYYY-MM-DD HH:mm"
          )
        : dayjs(event.start),
      meetingStartTime: event.startTime
        ? dayjs(event.startTime, "HH:mm")
        : dayjs(event.start),
      meetingType: event.meetingType || "offline",
      meetingLocation: event.location || "",
      meetingProvider:
        event.meetingType === "online"
          ? event.meetingProvider || "google"
          : "",
    });

    // Set selected contact/lead directly from event data
    if (event.contact_id && event.contact_name) {
      const contactOption = {
        value: event.contact_id,
        label: `${event.contact_name} (${event.contact_email || "No Email"
          }) - ${event.category === "lead" ? "Lead" : "Contact"}`,
        category: event.category || "contact",
      };
      setSelectedOption(contactOption);

      // Add this contact to allContacts so it appears in the dropdown
      setAllContacts([contactOption]);
    }
  };

  const handleDeleteMeeting = async (event) => {
    Modal.confirm({
      title: "Delete Meeting",
      content: `Are you sure you want to delete "${event.title}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          await dispatch(
            deleteMeeting({
              contact_id: event.contact_id,
              meeting_id: event.event_id,
              category: event.category || "contact",
            })
          ).unwrap();

          // Refresh events
          dispatch(profileEvents());
        } catch (error) {
          console.error("Error deleting meeting:", error);
        }
      },
    });
  };

  const handleMeetingInputChange = (name, value) => {
    setMeetingFormData({
      ...meetingFormData,
      [name]: value,
    });
  };
  const handleChange = (option) => {
    setSelectedOption(option);
  };

  const handleSearchChange = (inputValue) => {
    setSearchQuery(inputValue);
  };

  const resetMeetingForm = () => {
    setMeetingFormData({
      meeting_id: "",
      meetingDescription: "",
      meetingTitle: "",
      meetingStartDate: dayjs(),
      meetingStartTime: dayjs("00:00:00", "HH:mm:ss"),
      meetingProvider: "",
      meetingLocation: "",
    });
    setSelectedOption(null);
    setSearchQuery("");
    // Clear contacts list so previous search/result doesn't persist
    setAllContacts([]);

  };
  useEffect(() => {
    let elements = Array.from(
      document.getElementsByClassName("react-datepicker-wrapper")
    );
    elements.map((element) => element.classList.add("width-100"));
  }, []);

  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const getCalendarGrid = () => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startDay = startOfMonth.startOf("week");
    const endDay = endOfMonth.endOf("week");
    const days = [];
    let day = startDay;
    while (day.isBefore(endDay) || day.isSame(endDay, "day")) {
      days.push(day);
      day = day.add(1, "day");
    }
    return days;
  };

  const getEventDateTime = (event) =>
    dayjs(
      `${dayjs(event.start).format("YYYY-MM-DD")} ${event.startTime}`,
      "YYYY-MM-DD HH:mm"
    );

  const eventsByDate = Array.isArray(events)
    ? events
      ?.slice()
      .sort((a, b) => {
        const now = dayjs().valueOf();
        return (
          Math.abs(getEventDateTime(a).valueOf() - now) -
          Math.abs(getEventDateTime(b).valueOf() - now)
        );
      })
      .reduce((acc, event) => {
        const date = dayjs(event.start).format("YYYY-MM-DD");
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
        return acc;
      }, {})
    : {};

  const days = getCalendarGrid();

  return (
    <>
      <div className={isCalendar ? "page-wrapper" : ""}>
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card ">
                <div className="card-header mb-4">
                  {/* <div className="page-header"> */}
                  <div className="row align-items-center w-100">
                    <div className="col-lg-10 col-sm-12">
                      <h3 className="page-title">
                        {isCalendar ? "Calendar" : ""}
                      </h3>
                    </div>
                    <div className="col-lg-2 col-sm-12 d-flex justify-content-end p-0 gap-2">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleRefresh}
                        disabled={refreshing || isInitialLoad}
                      >
                        {refreshing ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <ReloadOutlined className="me-1" />
                        )}
                        Refresh
                      </button>
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={() => {
                          setIsMeetingModalOpen(true)
                          resetMeetingForm()
                        }}
                      >
                        Create Event
                      </Link>
                    </div>
                  </div>
                </div>

                {/* </div> */}
                <div className="row">
                  <div className="col-12 col-md-6 order-2 order-md-1">
                    {selectedDate && (
                      <div className="selected-events mt-6 ps-4">
                        <h5 className="text-lg font-semibold mb-3 text-gray-700 ">
                          Events on {dayjs(selectedDate).format("DD MMM YYYY")}
                        </h5>

                        {isInitialLoad ? (
                          <div style={{ height: 430, overflowY: "hidden" }}>
                            {Array.from({ length: 3 }).map((_, i) => (
                              <div key={i} className="card border-start border-primary border-3 mb-3 shadow-sm" style={{ borderRadius: 8 }}>
                                <div className="card-body p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="contact-skel" style={{ width: 80, height: 16, borderRadius: 4 }} />
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <div className="contact-skel" style={{ width: 32, height: 32, borderRadius: 6 }} />
                                      <div className="contact-skel" style={{ width: 32, height: 32, borderRadius: 6 }} />
                                    </div>
                                  </div>
                                  <div className="contact-skel" style={{ width: "70%", height: 16, borderRadius: 4, marginBottom: 10 }} />
                                  <div className="contact-skel" style={{ width: "90%", height: 12, borderRadius: 4, marginBottom: 6 }} />
                                  <div className="contact-skel" style={{ width: "55%", height: 12, borderRadius: 4 }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : eventsByDate[selectedDate]?.length > 0 ? (
                          <div
                            style={{
                              height: "430px",
                              overflow: "auto",
                              overflowX: "hidden"
                            }}
                          >
                            {eventsByDate[selectedDate].map((event) => (
                              <div
                                key={event.event_id}
                                className="card border-start border-primary border-3 mb-3 shadow-sm"
                                style={{ borderRadius: "8px" }}
                              >
                                <div className="card-body p-3">
                                  {/* Header with time and actions */}
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="d-flex align-items-center gap-2">
                                      <i
                                        className="ti ti-clock text-primary"
                                        style={{ fontSize: "18px" }}
                                      ></i>
                                      <span className="fw-semibold text-primary">
                                        {getFormattedDate(event.startTime)}
                                      </span>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-light p-1"
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                        }}
                                        onClick={() => handleEditMeeting(event)}
                                        title="Edit Meeting"
                                      >
                                        <i
                                          className="ti ti-edit"
                                          style={{ fontSize: "16px" }}
                                        ></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-light p-1"
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                        }}
                                        onClick={() =>
                                          handleDeleteMeeting(event)
                                        }
                                        title="Delete Meeting"
                                      >
                                        <i
                                          className="ti ti-trash text-danger"
                                          style={{ fontSize: "16px" }}
                                        ></i>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Meeting title */}
                                  <h6 className=" fw-bold text-dark mb-3">
                                    {event.title}
                                  </h6>


                                  {console.log(event, "eventtttnn")}

                                  {/* Description */}

                                  {event.description && (
                                    <p
                                      className="text-muted small mb-0"
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                      }}
                                    >
                                      {event.description}
                                    </p>
                                  )}
                                  {/* Contact/Lead info */}
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <i
                                      className="ti ti-user"
                                      style={{
                                        fontSize: "16px",
                                        color: "#6c757d",
                                      }}
                                    ></i>
                                    <span className="text-muted small">

                                      Owner : {event.owner_name || "Unknown"}
                                    </span>
                                    <i
                                      className="ti ti-user"
                                      style={{
                                        fontSize: "16px",
                                        color: "#6c757d",
                                      }}
                                    ></i>
                                    <span className="text-muted small">
                                      {event.category === "lead"
                                        ? "Lead"
                                        : "Contact"} Name : {event.contact_name || "Unknown"}
                                    </span>
                                    <span
                                      className={`badge ${event.category === "lead"
                                        ? "bg-info"
                                        : "bg-success"
                                        } ms-auto`}
                                      style={{
                                        fontSize: "10px",
                                        padding: "4px 8px",
                                      }}
                                    >
                                      {event.category === "lead"
                                        ? "Lead"
                                        : "Contact"}
                                    </span>
                                  </div>
                                  {event.meetingType == "offline" && event?.location && (
                                    <>
                                      <span
                                        className="cursor-pointer text-gray-400 hover:text-primary transition-transform hover:scale-110 ms-2"
                                      >


                                        <IoLocationSharp className="me-1" />
                                        {event?.location}
                                      </span></>

                                  )}


                                  {event.meetingType == "online" && event?.link && (
                                    <> <a
                                      href={event?.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary underline"
                                    >
                                      Join Meeting
                                    </a>
                                      <span
                                        onClick={() => handleCopy(event)}
                                        className="cursor-pointer text-gray-400 hover:text-primary transition-transform hover:scale-110 ms-2"
                                        title="Copy link"
                                      >
                                        {console.log(event, "hjgfdghvjb")
                                        }
                                        {copiedId === event.event_id ? (
                                          // <FiCheck size={16} className="text-green-500" />
                                          "Link Copied"
                                        ) : (
                                          <FiCopy size={16} />
                                        )}
                                      </span></>

                                  )}

                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "40px 16px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: 16,
                              }}
                            >
                              <i className="ti ti-calendar-off" style={{ fontSize: 28, color: "#7c3aed" }} />
                            </div>
                            <p style={{ fontWeight: 600, color: "#374151", marginBottom: 4, fontSize: 15 }}>
                              No meetings on {dayjs(selectedDate).format("MMM D")}
                            </p>
                            <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 20 }}>
                              {dayjs(selectedDate).isBefore(today, "day")
                                ? "This date has passed"
                                : "Free slot — schedule something?"}
                            </p>
                            {!dayjs(selectedDate).isBefore(today, "day") && (
                              <button
                                className="voycell-empty-btn"
                                onClick={() => {
                                  setMeetingFormData((prev) => ({
                                    ...prev,
                                    meeting_id: "",
                                    meetingTitle: "",
                                    meetingDescription: "",
                                    meetingProvider: "",
                                    meetingLocation: "",
                                    meetingStartDate: dayjs(selectedDate).isSame(dayjs(), "day")
                                      ? dayjs()
                                      : dayjs(selectedDate).hour(9).minute(0).second(0),
                                    meetingStartTime: dayjs(selectedDate).isSame(dayjs(), "day")
                                      ? dayjs()
                                      : dayjs("09:00:00", "HH:mm:ss"),
                                  }));
                                  setSelectedOption(null);
                                  setIsMeetingModalOpen(true);
                                }}
                              >
                                <i className="ti ti-plus" />
                                Add Meeting
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-6 order-1 order-md-2">
                    <div className="custom-calendar-wrapper mb-4">
                      <div className="calendar-header">
                        <div
                          onClick={() =>
                            setCurrentMonth(currentMonth.subtract(1, "month"))
                          }
                          className="calendar-nav"
                        >
                          {"<"}
                        </div>
                        <span>{currentMonth.format("MMMM YYYY")}</span>
                        <div
                          onClick={() =>
                            setCurrentMonth(currentMonth.add(1, "month"))
                          }
                          className="calendar-nav"
                        >
                          {">"}
                        </div>
                      </div>
                      <div className="calendar-grid">
                        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                          <div
                            className="calendar-cell calendar-cell-header"
                            key={d}
                          >
                            {d}
                          </div>
                        ))}
                        {days.map((day, idx) => {
                          const isCurrentMonth =
                            day.month() === currentMonth.month();
                          const isToday = day.isSame(today, "day");
                          const isSelected =
                            day.format("YYYY-MM-DD") === selectedDate;
                          const dayEvents =
                            eventsByDate[day.format("YYYY-MM-DD")] || [];

                          return (
                            <div
                              key={idx}
                              className={`calendar-cell${isCurrentMonth ? "" : " calendar-cell-outside"
                                }${isToday ? " calendar-cell-today" : ""}${isSelected ? " calendar-cell-selected" : ""
                                }`}
                              onClick={() =>
                                setSelectedDate(day.format("YYYY-MM-DD"))
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <div className="calendar-date">{day.date()}</div>
                              {dayEvents.length > 0 && (
                                <div
                                  className={`calendar-event-indicator`}
                                  style={{
                                    backgroundColor: isToday ? "white" : "",
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title={meetingFormData.meeting_id ? "Edit Meeting" : "Add New Meeting"}
        open={isMeetingModalOpen}
        onCancel={() => {
          resetMeetingForm();
          setIsMeetingModalOpen(false);
        }}
        footer={null}
        destroyOnClose
        centered
      >
        <form>
          <div className="mb-3">
            <label className="col-form-label">
              Title <span className="text-danger"> *</span>
            </label>
            <input
              className="form-control"
              name="meetingTitle"
              value={meetingFormData.meetingTitle}
              onChange={(e) => {
                handleMeetingInputChange(e.target.name, e.target.value);
              }}
              type="text"
            />
          </div>

          <div className="mb-3">
            <label className="col-form-label">
              Description <span className="text-danger"> *</span>
            </label>
            <textarea
              className="form-control"
              rows={4}
              name="meetingDescription"
              value={meetingFormData.meetingDescription}
              onChange={(e) =>
                handleMeetingInputChange(e.target.name, e.target.value)
              }
            />
          </div>

          <div
            className="mb-3"
            style={{ zIndex: 9999, position: "relative" }}
          >
            <label className="col-form-label">
              Select Contact <span className="text-danger"> *</span>
            </label>
            <Select
              value={selectedOption}
              className="select2"
              options={allContacts}
              name="allContacts"
              onChange={handleChange}
              onInputChange={handleSearchChange}
              placeholder="Search and select contact..."
              classNamePrefix="react-select"
              isSearchable={true}
              isDisabled={!!meetingFormData.meeting_id}
              isLoading={isLoadingContacts}
              loadingMessage={() => "Searching contacts..."}
              noOptionsMessage={() => {
                if (!searchQuery || searchQuery.trim().length < 1) {
                  return "Type at least 1 character to search contacts";
                }
                return "No contacts found";
              }}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
            />
          </div>

          <div
            className="row"
            style={{ zIndex: 1, position: "relative" }}
          >

            <div className="col-md-12 mb-3">
              <label className="col-form-label">Start Date & Time</label>


              {/* <DatePicker
                className="form-control datetimepicker deals-details"
                format="DD-MM-YYYY hh:mm A"
                showTime={{
                  use12Hours: true,
                  format: "hh:mm A",
                }}
                value={meetingFormData.meetingStartDate || null}
                onChange={(date) => {
                  if (!date) {
                    setMeetingFormData((prev) => ({
                      ...prev,
                      meetingStartDate: null,
                      meetingStartTime: null,
                    }));
                    return;
                  }


                  setMeetingFormData((prev) => ({
                    ...prev,
                    meetingStartDate: date,
                    meetingStartTime: date,
                  }));
                }}
              /> */}
              <DatePicker
                className="form-control datetimepicker deals-details"
                format="DD-MM-YYYY hh:mm A"
                showTime={{
                  use12Hours: true,
                  format: "hh:mm A",
                }}
                value={meetingFormData.meetingStartDate || null}
                disabledDate={(current) => {
                  // Disable all dates before today
                  return current && current < dayjs().startOf("day");
                }}
                disabledTime={(current) => {
                  if (!current) return {};

                  const now = dayjs();

                  // Only restrict time if the selected date is today
                  if (current.isSame(now, "day")) {
                    const currentHour = now.hour();
                    const currentMinute = now.minute();

                    return {
                      // Disable all past hours
                      disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
                      // Only disable minutes in the current hour
                      disabledMinutes: (selectedHour) =>
                        selectedHour === currentHour
                          ? Array.from({ length: currentMinute }, (_, i) => i)
                          : [],
                      disabledSeconds: () => [], // allow any seconds
                    };
                  }

                  // No restrictions for other dates
                  return {
                    disabledHours: () => [],
                    disabledMinutes: () => [],
                    disabledSeconds: () => [],
                  };
                }}
                onChange={(date) => {
                  if (!date) {
                    setMeetingFormData((prev) => ({
                      ...prev,
                      meetingStartDate: null,
                      meetingStartTime: null,
                    }));
                    return;
                  }

                  setMeetingFormData((prev) => ({
                    ...prev,
                    meetingStartDate: date,
                    meetingStartTime: date,
                  }));
                }}
              />


            </div>

            <div className="mb-3">
              <label className="col-form-label">Meeting Type</label>
              <Select
                className="select2"
                options={meetingType}
                value={
                  meetingFormData.meetingType
                    ? meetingType.find(
                      (option) =>
                        option.value.toLowerCase() ===
                        meetingFormData.meetingType
                    )
                    : null
                }
                onChange={(option) => {
                  setMeetingFormData({
                    ...meetingFormData,
                    meetingType: option.value.toLowerCase(),
                    meetingLocation:
                      option.value.toLowerCase() === "online"
                        ? ""
                        : meetingFormData.meetingLocation,
                  });
                  setSelectedMeetingType(option.value);
                }}
                placeholder="Choose"
                classNamePrefix="react-select"
              />
            </div>

            {meetingFormData.meetingType === "offline" && (
              <div className="mb-3">
                <label className="col-form-label">Location</label>
                <input
                  className="form-control"
                  type="text"
                  value={meetingFormData.meetingLocation}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      meetingLocation: e.target.value,
                    })
                  }
                />
              </div>
            )}
            {meetingFormData.meetingType === "online" && (
              <div className="col-md-12 mb-3">
                <label className="col-form-label">
                  Meeting Provider <span className="text-danger">*</span>
                </label>

                <Select
                  className="select2"
                  options={[
                    { value: "google", label: "Google Meet" },
                    { value: "zoom", label: "Zoom" },
                  ]}
                  value={
                    meetingFormData.meetingProvider
                      ? [
                        { value: "google", label: "Google Meet" },
                        { value: "zoom", label: "Zoom" },
                      ].find(
                        (option) =>
                          option.value === meetingFormData.meetingProvider
                      )
                      : null
                  }
                  onChange={(option) =>
                    setMeetingFormData((prev) => ({
                      ...prev,
                      meetingProvider: option.value,
                    }))
                  }
                  placeholder="Select Provider"
                  classNamePrefix="react-select"
                />

                <div className="alert alert-info d-flex align-items-center mt-2">
                  <i className="ti ti-info-circle me-2"></i>
                  <span>
                    {meetingFormData.meetingProvider === "zoom"
                      ? "A Zoom meeting link will be automatically generated when you save this meeting."
                      : meetingFormData.meetingProvider === "google"
                        ? "A Google Meet link will be automatically generated when you save this meeting."
                        : "Select a provider to create an online meeting link automatically."}
                  </span>
                </div>
              </div>
            )}


          </div>

          <div className="col-lg-12 text-end modal-btn mt-4">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                resetMeetingForm();
                setIsMeetingModalOpen(false);
              }}
            >
              Cancel
            </button>

            <button
              className="btn btn-primary"
              type="submit"
              disabled={isLoading}
              onClick={(e) => {
                e.preventDefault()
                handleMeetingSubmit()
              }}
            >
              {isLoading && <Spin indicator={<LoadingOutlined spin style={{ color: "white", marginRight: 5 }} />} size="small" />}{meetingFormData?.meeting_id ? "Save changes" : "Create Meeting"}
            </button>
          </div>
        </form>
      </Modal>

    </>
  );
};

export default Calendar;
